/**
 * Client-side prediction of the WHOLE room — own car and remote cars live on
 * one coherent timeline (the local player's predicted present).
 *
 * Own car: inputs are applied locally the instant they are sampled (zero
 * perceived latency), numbered, and kept until the server acknowledges them.
 *
 * Remote cars: dead reckoning with the same shared physics — each car is
 * advanced with the last input the server applied to it (carried in every
 * snapshot), which is EXACTLY what the server itself does when a player's next
 * input has not arrived. Car-car contacts are resolved locally with the same
 * symmetric resolver and the same fixed pair order as the server.
 *
 * Why: with remote cars interpolated ~100 ms in the past (the previous model),
 * the server adjudicated contacts on positions the player never saw — at
 * 60 ms of ping, an opponent was displayed two car lengths away from where the
 * server had it, and every contact became a surprise jolt. On one timeline,
 * what touches on screen is what the server rules on; the residual error is
 * only the opponent's input changes within one RTT, absorbed by a per-car
 * decaying visual offset — never a visible teleport.
 *
 * On each snapshot the whole world is rewound to the authoritative states and
 * the unacknowledged own inputs are replayed (remotes replay with their held
 * inputs). With static configs and no contact the own-car replay is bit-exact,
 * so corrections are normally invisible.
 */

import type {
  CarInput,
  CarSnapshot,
  CarState,
  ClosestPointResult,
  InputPacketEntry,
  SurfaceConfigs,
  SurfaceId,
  Track,
} from '@microligue/shared';
import {
  DT,
  INPUT_REDUNDANCY,
  closestPoint,
  lerp,
  lerpAngle,
  resolveCarCarCollision,
  resolveTrackCollision,
  stepCar,
  wrapAngle,
} from '@microligue/shared';

/**
 * Beyond this positional error (px), snap instead of smoothing. 160 rather
 * than 120: a head-on contact correction easily reaches 120-150 px of error
 * and snapping mid-impact is a visible teleport at the worst possible moment
 * (audit multijoueur F4).
 */
const SNAP_DISTANCE = 160;
/** 1/s — decay rate of the visual correction offsets (own and remote cars). */
const ERROR_DECAY = 10;
/**
 * px/s — floor of the ceiling on how fast a positional correction may
 * visually catch up. Without a ceiling, a large offset (network hiccup,
 * server fast-forwarding a lagging player) resorbed at ERROR_DECAY looked
 * like a huge turbo burst (~1000 px/s). But a FIXED 240 px/s ceiling made
 * CONTACT corrections mushy: a 60-80 px post-impact offset took 250-330 ms to
 * settle, with the displayed pose diverging from the physics pose the whole
 * time. The effective ceiling now scales with the car's own speed
 * (max(240, 1.0·|v|)): fast cars — the ones whose contacts produce big
 * corrections — settle in < 100 ms, while the anti-turbo guard remains for
 * slow/parked cars (audit multijoueur F4).
 *
 * Factor 1.0 (was 0.75, 2026-07-21): in a SATURATED ack stall the remote sim
 * freezes at the extrapolation cap and ALL its displayed motion flows through
 * this ceiling — at 0.75 the opponent visually drove at 75% of its true
 * speed for the whole stall. Unnoticed while walls scrubbed speeds low; the
 * bumper-car rebounds keep speeds high and exposed it (locked by the uplink
 * -stall test). At 1.0 the stalled flow matches true speed, and the ceiling
 * still bounds any correction to ≈ the car's own pace — no turbo look.
 */
const MAX_CORRECTION_SPEED = 240;
const CORRECTION_SPEED_FACTOR = 1.0;
/**
 * Ticks a remote car keeps dead-reckoning past its last snapshot (300 ms);
 * beyond, it holds its pose until fresher data arrives. This cap serves twice:
 * on a snapshot outage it stops the car from driving blind, and — crucial —
 * it bounds the replay horizon when OUR unacked input queue inflates (uplink
 * hiccup: the server stalls on our missing seq while snapshots keep flowing).
 * Extrapolating remotes by the full pending length in that state made every
 * opponent surge forward at ~2× speed until the backlog resorbed — the
 * "énorme accélération" seen in playtests. With the cap they advance at true
 * speed, simply held at a bounded lead ahead of their snapshot.
 */
const MAX_EXTRAPOLATION_TICKS = 18;
/**
 * Client-side cap on the unacked input queue. The server bounds it at 600
 * (MAX_SEQ_LEAD) but replaying hundreds of world ticks inside a snapshot
 * handler after a long uplink stall would freeze the frame in cascade (audit
 * multijoueur F9). Beyond the cap the oldest inputs are dropped — degraded
 * accuracy is absorbed by the usual correction offsets, or the snap.
 */
const MAX_PENDING_INPUTS = 120;

/** A remote car's predicted pose, ready to draw. */
export interface RemoteDisplayState {
  x: number;
  y: number;
  angle: number;
  vx: number;
  vy: number;
  surface: SurfaceId;
}

interface RemoteCar {
  sim: CarState;
  /** State before the latest advance — render interpolation between ticks. */
  prev: CarState;
  surface: SurfaceId;
  /** Last input the server applied (held while dead reckoning). */
  input: CarInput;
  errX: number;
  errY: number;
  errAngle: number;
  /** Ticks advanced since the snapshot that seeded `sim` (extrapolation cap). */
  ticksSinceSnap: number;
  /** False until the first snapshot mentions this car. */
  seeded: boolean;
}

export class WorldPredictor {
  state: CarState;
  surface: SurfaceId;
  /** Wall query of the own car's latest advance (lap mirror reuse, F8). */
  lastOwnQuery: ClosestPointResult | null = null;

  /** Unacked input count — the pace servo's process variable. */
  get pendingLength(): number {
    return this.pending.length;
  }

  /** Raw predicted (physics) pose of a remote — diagnostics/benches only. */
  remoteSimPose(id: number): { x: number; y: number } | null {
    const r = this.remotes.get(id);
    return r && r.seeded ? { x: r.sim.x, y: r.sim.y } : null;
  }
  /**
   * Whole-circuit surface override (admin test tuning). When set, every car
   * drives with this surface everywhere, matching the server — so no config
   * mismatch, no rubber-banding. null = follow the track's real surfaces.
   */
  overrideSurface: SurfaceId | null = null;

  private seq = 0;
  /** Snapshots acked below this seq predate a locally predicted reset. */
  private suspendReconcileBelowAck = 0;
  private readonly pending: { seq: number; input: CarInput }[] = [];
  private errX = 0;
  private errY = 0;
  private errAngle = 0;
  private readonly remotes = new Map<number, RemoteCar>();

  constructor(
    private readonly track: Track,
    private readonly surfaces: SurfaceConfigs,
    initial: CarState,
    private readonly selfId: number,
  ) {
    this.state = { ...initial };
    this.surface = this.surfaceOf(closestPoint(track, this.state).surface);
  }

  /** Surface actually driven at a track point — the override wins when set. */
  private surfaceOf(sample: SurfaceId): SurfaceId {
    return this.overrideSurface ?? sample;
  }

  /** Registers a remote car (roster join). It stays invisible until seeded. */
  addRemote(id: number): void {
    if (id === this.selfId || this.remotes.has(id)) return;
    this.remotes.set(id, {
      sim: { x: 0, y: 0, angle: 0, vx: 0, vy: 0 },
      prev: { x: 0, y: 0, angle: 0, vx: 0, vy: 0 },
      surface: this.surfaceOf('asphalt'),
      input: { throttle: 0, brake: 0, steer: 0 },
      errX: 0,
      errY: 0,
      errAngle: 0,
      ticksSinceSnap: 0,
      seeded: false,
    });
  }

  removeRemote(id: number): void {
    this.remotes.delete(id);
  }

  /**
   * Advances one predicted tick of the whole world and returns the redundant
   * input packet to send.
   */
  tick(input: CarInput): InputPacketEntry[] {
    this.seq++;
    this.pending.push({ seq: this.seq, input: { ...input } });
    while (this.pending.length > MAX_PENDING_INPUTS) this.pending.shift();
    for (const r of this.remotes.values()) {
      if (r.seeded) r.prev = { ...r.sim };
    }
    this.advanceWorld(input);

    const start = Math.max(0, this.pending.length - INPUT_REDUNDANCY);
    return this.pending.slice(start).map((p) => ({
      seq: p.seq,
      throttle: p.input.throttle,
      brake: p.input.brake,
      steer: p.input.steer,
    }));
  }

  /**
   * Local mirror of the server-side R reset. With a pose (last crossed
   * checkpoint, computed by the caller with the same rules as the server),
   * respawn there; otherwise nearest centerline (lap not armed yet).
   */
  predictReset(pose: CarState | null = null): void {
    // In-flight snapshots were emitted BEFORE the server applied our reset:
    // reconciling against them would yank the car back to the crash site
    // (visible yo-yo) and derail the lap mirror. Ignore them until the server
    // acknowledges the first post-reset input.
    this.suspendReconcileBelowAck = this.seq + 1;
    if (pose) {
      this.state = { ...pose };
      this.surface = this.surfaceOf(closestPoint(this.track, this.state).surface);
    } else {
      const q = closestPoint(this.track, this.state);
      this.state = {
        x: q.point.x,
        y: q.point.y,
        angle: Math.atan2(q.tangent.y, q.tangent.x),
        vx: 0,
        vy: 0,
      };
      this.surface = this.surfaceOf(q.surface);
    }
    this.errX = 0;
    this.errY = 0;
    this.errAngle = 0;
  }

  /**
   * Authoritative snapshot: rewind the WHOLE world onto it and replay the
   * unacknowledged own inputs — remotes replay alongside with their held
   * inputs, so contacts during the replay happen on coherent positions.
   *
   * `alpha` is the caller's CURRENT render phase (accumulator/DT ∈ [0,1)):
   * snapshots land between two frames, and the pose the player actually sees
   * is lerp(prev, sim, alpha). Capturing/re-anchoring the error offsets at
   * that exact pose (instead of the full-tick pose) is what removes the 30 Hz
   * ~5-11 px forward-jump-then-freeze on every car (audit multijoueur F1).
   */
  applySnapshot(cars: CarSnapshot[], ack: number, alpha = 1): void {
    while (this.pending.length > 0 && (this.pending[0] as { seq: number }).seq <= ack) {
      this.pending.shift();
    }

    // Seed the remotes; remember their previous DISPLAYED pose for smoothing.
    const prevDisplayed = new Map<number, { x: number; y: number; angle: number }>();
    let own: CarSnapshot | undefined;
    for (const car of cars) {
      if (car.id === this.selfId) {
        own = car;
        continue;
      }
      const r = this.remotes.get(car.id);
      if (!r) continue; // roster message not processed yet — next snapshot
      if (r.seeded) {
        prevDisplayed.set(car.id, {
          x: lerp(r.prev.x, r.sim.x, alpha) + r.errX,
          y: lerp(r.prev.y, r.sim.y, alpha) + r.errY,
          angle: lerpAngle(r.prev.angle, r.sim.angle, alpha) + r.errAngle,
        });
      }
      r.sim = { x: car.x, y: car.y, angle: car.angle, vx: car.vx, vy: car.vy };
      r.prev = { ...r.sim };
      r.surface = this.surfaceOf(car.surface);
      r.input = { throttle: car.input.throttle, brake: car.input.brake, steer: car.input.steer };
      r.ticksSinceSnap = 0;
      r.seeded = true;
    }

    // Own rewind is suspended right after a predicted reset (see predictReset);
    // the remotes still re-extrapolate to the local timeline either way.
    const rewindOwn = own !== undefined && ack >= this.suspendReconcileBelowAck;
    let before: CarState | null = null;
    if (rewindOwn && own) {
      before = this.state;
      this.state = { x: own.x, y: own.y, angle: own.angle, vx: own.vx, vy: own.vy };
      // own.surface already reflects the server override; re-assert it locally
      // so a just-changed override applies even with nothing to replay.
      this.surface = this.surfaceOf(own.surface);
    }

    for (const p of this.pending) {
      // Keep each remote's pre-advance state: after the replay, `prev` is the
      // second-to-last world state and the alpha interpolation stays seamless.
      for (const r of this.remotes.values()) {
        if (r.seeded) r.prev = { ...r.sim };
      }
      this.advanceWorld(rewindOwn ? p.input : null);
    }

    if (before) {
      const dx = before.x - this.state.x;
      const dy = before.y - this.state.y;
      const err = Math.sqrt(dx * dx + dy * dy);
      if (err > SNAP_DISTANCE) {
        this.errX = 0;
        this.errY = 0;
        this.errAngle = 0;
      } else {
        // Carry the previous visual position so the correction is invisible.
        this.errX = before.x + this.errX - this.state.x;
        this.errY = before.y + this.errY - this.state.y;
        this.errAngle = wrapAngle(before.angle + this.errAngle - this.state.angle);
      }
    }

    for (const [id, r] of this.remotes) {
      if (!r.seeded) continue;
      const pd = prevDisplayed.get(id);
      if (pd) {
        // Re-anchor the offset against the pose the very next frame will show
        // at the SAME alpha — the displayed car does not move at all on the
        // snapshot itself; the correction then eases in via decayError.
        const shownX = lerp(r.prev.x, r.sim.x, alpha);
        const shownY = lerp(r.prev.y, r.sim.y, alpha);
        const shownAngle = lerpAngle(r.prev.angle, r.sim.angle, alpha);
        const dx = pd.x - shownX;
        const dy = pd.y - shownY;
        if (Math.sqrt(dx * dx + dy * dy) > SNAP_DISTANCE) {
          r.errX = 0;
          r.errY = 0;
          r.errAngle = 0;
        } else {
          r.errX = dx;
          r.errY = dy;
          r.errAngle = wrapAngle(pd.angle - shownAngle);
        }
      } else {
        r.errX = 0;
        r.errY = 0;
        r.errAngle = 0;
      }
    }
  }

  /**
   * Hard resync (the server teleported us: race grid). Drop the unacked inputs
   * — replaying them onto the grid pose would fight the server, which is
   * neutralizing inputs anyway during the countdown.
   */
  clearPending(): void {
    this.pending.length = 0;
    this.suspendReconcileBelowAck = 0;
    this.errX = 0;
    this.errY = 0;
    this.errAngle = 0;
  }

  /** Decays every visual correction offset (called once per rendered frame). */
  decayError(dt: number): void {
    // dt ≤ 0 : ne JAMAIS entrer dans l'exponentielle — exp(-k·dt) > 1 ferait
    // CROÎTRE les offsets (dt=-2,5 s → ×10¹⁰ : voiture catapultée hors du
    // monde, bug propriétaire du 19/07 via le mélange d'horloges rAF/perf).
    if (!(dt > 0)) return;
    const k = Math.exp(-ERROR_DECAY * dt);
    // Position: exponential decay, capped at a speed that scales with the
    // car's own velocity — fast contact corrections settle quickly, parked
    // cars keep the anti-turbo guard.
    const scaleFor = (ex: number, ey: number, vx: number, vy: number): number => {
      const err = Math.sqrt(ex * ex + ey * ey);
      if (err <= 1e-9) return 0;
      const ceiling = Math.max(
        MAX_CORRECTION_SPEED,
        CORRECTION_SPEED_FACTOR * Math.sqrt(vx * vx + vy * vy),
      );
      const delta = Math.min(err * (1 - k), ceiling * dt);
      return Math.max(0, (err - delta) / err);
    };
    const own = scaleFor(this.errX, this.errY, this.state.vx, this.state.vy);
    this.errX *= own;
    this.errY *= own;
    this.errAngle *= k;
    for (const r of this.remotes.values()) {
      const s = scaleFor(r.errX, r.errY, r.sim.vx, r.sim.vy);
      r.errX *= s;
      r.errY *= s;
      r.errAngle *= k;
    }
  }

  /** Own state to draw: prediction plus the decaying correction offset. */
  displayState(): CarState {
    return {
      x: this.state.x + this.errX,
      y: this.state.y + this.errY,
      angle: wrapAngle(this.state.angle + this.errAngle),
      vx: this.state.vx,
      vy: this.state.vy,
    };
  }

  /** Current own visual correction offset (for render interpolation). */
  errorOffset(): { x: number; y: number; angle: number } {
    return { x: this.errX, y: this.errY, angle: this.errAngle };
  }

  /**
   * A remote car's pose to draw: predicted state (interpolated between the two
   * latest ticks by `alpha`) plus its smoothed correction offset. null while
   * the car has never appeared in a snapshot.
   */
  remoteDisplay(id: number, alpha: number): RemoteDisplayState | null {
    const r = this.remotes.get(id);
    if (!r || !r.seeded) return null;
    return {
      x: lerp(r.prev.x, r.sim.x, alpha) + r.errX,
      y: lerp(r.prev.y, r.sim.y, alpha) + r.errY,
      angle: wrapAngle(lerpAngle(r.prev.angle, r.sim.angle, alpha) + r.errAngle),
      vx: r.sim.vx,
      vy: r.sim.vy,
      surface: r.surface,
    };
  }

  /**
   * One world tick, mirroring the server's stepOnce: every car advances
   * independently (step + wall), then car-car pairs are resolved in the fixed
   * id order with a wall re-resolution — a shove can never push a car out of
   * the band. `ownInput` null = advance the remotes only (own rewind
   * suspended after a predicted reset).
   */
  private advanceWorld(ownInput: CarInput | null): void {
    if (ownInput) {
      const cfg = this.surfaces[this.surface];
      const stepped = stepCar(this.state, ownInput, cfg, DT);
      const wall = resolveTrackCollision(stepped, this.track, cfg);
      this.state = wall.state;
      this.surface = this.surfaceOf(wall.query.surface);
      // Reused by the caller's lap mirror — same semantics as the server,
      // which times laps on the post-advance, pre-pairs query. Saves one full
      // coarse centerline scan per tick (audit multijoueur F8).
      this.lastOwnQuery = wall.query;
    }

    for (const r of this.remotes.values()) {
      if (!r.seeded || r.ticksSinceSnap >= MAX_EXTRAPOLATION_TICKS) continue;
      r.ticksSinceSnap++;
      const cfg = this.surfaces[r.surface];
      const stepped = stepCar(r.sim, r.input, cfg, DT);
      const wall = resolveTrackCollision(stepped, this.track, cfg);
      r.sim = wall.state;
      r.surface = this.surfaceOf(wall.query.surface);
    }

    // Car-vs-car in the server's deterministic pair order (ids ascending).
    interface PairCar {
      id: number;
      remote: RemoteCar | null;
    }
    const order: PairCar[] = [];
    if (ownInput) order.push({ id: this.selfId, remote: null });
    for (const [id, r] of this.remotes) {
      if (r.seeded) order.push({ id, remote: r });
    }
    if (order.length < 2) return;
    order.sort((a, b) => a.id - b.id);

    const stateOf = (c: PairCar): CarState => (c.remote ? c.remote.sim : this.state);
    const surfaceOfCar = (c: PairCar): SurfaceId => (c.remote ? c.remote.surface : this.surface);
    const setCar = (c: PairCar, state: CarState, surface: SurfaceId): void => {
      if (c.remote) {
        c.remote.sim = state;
        c.remote.surface = surface;
      } else {
        this.state = state;
        this.surface = surface;
      }
    };

    for (let i = 0; i < order.length; i++) {
      for (let j = i + 1; j < order.length; j++) {
        const A = order[i] as PairCar;
        const B = order[j] as PairCar;
        const cfgA = this.surfaces[surfaceOfCar(A)];
        const cfgB = this.surfaces[surfaceOfCar(B)];
        const bounciness = (cfgA.carBounciness + cfgB.carBounciness) / 2;
        const res = resolveCarCarCollision(stateOf(A), stateOf(B), bounciness);
        if (!res.hit) continue;
        const wallA = resolveTrackCollision(res.a, this.track, cfgA);
        const wallB = resolveTrackCollision(res.b, this.track, cfgB);
        setCar(A, wallA.state, this.surfaceOf(wallA.query.surface));
        setCar(B, wallB.state, this.surfaceOf(wallB.query.surface));
      }
    }
  }
}
